import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useApiClient } from "../../../apiClient";
import { OrganizationDto } from "../../../models/organization/dto/organization.dto";
import { ReadState, classifyRead } from "../../_shared/readState";
import { useSelectedOrganization } from "./useSelectedOrganization";

export const MY_ORGANIZATIONS_QUERY_KEY = ['my-organizations'];
/**
 * The caller's organizations, each carrying `myRole` — the caller's role in
 * THAT org, which is what every role-dependent affordance should resolve from
 * (see `features/auth/roles.ts`).
 *
 * `enabled` exists so pre-auth callers (the `/` landing redirect) can mount the
 * hook without firing an unauthenticated request. It defaults to true so the
 * three original consumers are unaffected.
 */
export const useGetMyOrganizations = (enabled: boolean = true) => {
    const apiClient = useApiClient();
    const { selectedOrganization, setSelectedOrganization } = useSelectedOrganization();
    const result = useQuery({
        queryKey: MY_ORGANIZATIONS_QUERY_KEY,
        placeholderData: [],
        enabled,
        queryFn: async () => {
            return apiClient.fetchApi<OrganizationDto[], {}>({ method: 'GET', endpoint: `organizations/my-organizations` });
        },
    });

    useEffect(() => {
        if (!result.data) return;

        if (!selectedOrganization) {
            if (result.data.length > 0) {
                setSelectedOrganization(result.data[0]);
            }
            return;
        }

        // Keep the CALLER-scoped half of the stored org fresh.
        //
        // The auto-select above is deliberately gated on nothing being selected
        // (a background refetch must not snap the picker back to
        // organizations[0] — see OrganizationPicker's comment). But `myRole`
        // later moved onto this same DTO, and it inherited that freeze: this
        // query refetches on window focus and hands back an up-to-date role,
        // and every one of those answers was thrown away. `useUserRole` reads
        // `selectedOrganization.myRole`, so an operator demoted ADMIN → COACH
        // kept the entire admin UI — create buttons, destructive modals, the
        // configuration tab — for the LIFETIME OF THE TAB, which the SPA is
        // designed to keep open for days. Every click 403s, and 403 is
        // deliberately excluded from the sign-out path (apiClient), so nothing
        // self-corrects. That is the lockstep invariant inverted: the UI offers
        // exactly what the API refuses.
        //
        // Scope: the ROLE only. Re-storing on any other difference would fight
        // `mergeSelectedOrganization` and re-render on every refetch, and a
        // selected org that has vanished from the list is deliberately NOT
        // handled here — `placeholderData: []` means an in-flight refetch is
        // indistinguishable from "you were removed", so clearing on absence
        // would log people out of their own org mid-load.
        const fresh = result.data.find((org) => org.id === selectedOrganization.id);
        if (fresh && fresh.myRole !== undefined && fresh.myRole !== selectedOrganization.myRole) {
            setSelectedOrganization(fresh);
        }
    }, [result.data, selectedOrganization]);

    return result;
};

/**
 * Has `my-organizations` actually answered yet?
 *
 * Do NOT reach for `isLoading` here. `placeholderData: []` makes React Query v4
 * report `isLoading === false` on the very first render (`isLoading` is
 * `isFetching && !data`, and placeholder data counts as data), so an
 * `if (isLoading)` gate never fires — that is exactly why the spinner in
 * `OrganizationPicker` is dead code. `isPlaceholderData` is the flag that
 * distinguishes "the server said []" from "we invented [] while we wait".
 *
 * An ERROR counts as resolved: the caller must fall back to the role claim
 * rather than block forever behind a dead API.
 *
 * @param enabled pass `false` when the caller is not signed in — an unauthenticated
 *   session will never resolve, so it is reported as resolved immediately.
 */
export const useMyOrganizationsResolved = (enabled: boolean = true): boolean => {
    const { isPlaceholderData, isError } = useGetMyOrganizations(enabled);
    if (!enabled) return true;
    return !isPlaceholderData || isError;
};

/**
 * This read, classified — the four-state answer every consumer that renders an
 * affordance should branch on.
 *
 * It exists because the loading term for THIS hook is not the one the rest of
 * the campaign uses, and the difference is invisible at a call site. Every other
 * classified read in the dashboard passes `isInitialLoading`; here that flag is
 * permanently false, because `placeholderData: []` makes React Query overwrite
 * the status of an in-flight first fetch with `success`
 * (`queryObserver.js:357`, query-core 4.36.1 — measured in
 * `_shared/my-organizations-read-state.test.tsx`, "the premise"). So
 * `isLoading` and `isInitialLoading` are BOTH false while the real list is on
 * the wire, and the only flag that separates "the server said []" from "we
 * invented [] while we wait" is `isPlaceholderData`.
 *
 * Run 125 worked that out and wrote `isInitialLoading || isPlaceholderData` at
 * one call site, with the reasoning in a comment beside it. Run 126 needed the
 * same term at a second one and extracted it here instead of copying it: D59 —
 * a private copy of a shared decision is a fork, and this is the shape of fork
 * that fails silently, because a caller that reaches for the obvious
 * `isInitialLoading` gets a state that is wrong only while the read is in
 * flight. D62 is the same instruction from the other side: when a predicate
 * needs a fact the hook already knows, the hook is where it belongs, so the
 * next caller inherits the subtlety instead of re-deriving it.
 *
 * `isInitialLoading` is kept in the expression for the day the placeholder
 * leaves this hook, as `useGetLocations`' did at run 107: it is false today and
 * correct afterwards, so no call site needs a second edit then.
 *
 * `organizations` is the query's `data` unchanged — masking the placeholder
 * would be a second behaviour to reason about, and callers branch on `state`.
 */
export const useMyOrganizationsReadState = (
    enabled: boolean = true,
): MyOrganizationsRead => myOrganizationsReadState(useGetMyOrganizations(enabled));

export interface MyOrganizationsRead {
    state: ReadState;
    organizations: OrganizationDto[] | undefined;
}

/**
 * The derivation above, as a pure function of the query's own flags.
 *
 * Split out for one reason, and it is the reason D59 exists: the suites that
 * pin these consumers mock this whole module, so a hook-only helper would have
 * had to be re-implemented inside the mock — a second copy of the very
 * expression this extraction was made to de-duplicate, living in the file whose
 * job is to catch it changing. A pure export lets the mock call the REAL
 * derivation over its fixtures instead, and lets the term be asserted directly.
 */
export function myOrganizationsReadState({
    data,
    isError,
    isInitialLoading,
    isPlaceholderData,
}: {
    data: OrganizationDto[] | undefined;
    isError: boolean;
    isInitialLoading: boolean;
    isPlaceholderData: boolean;
}): MyOrganizationsRead {
    return {
        state: classifyRead(isError, isInitialLoading || isPlaceholderData, data),
        organizations: data,
    };
}
